/**
 * Format the owners of ONE account into a single dropdown label (#438).
 *
 * Used by the work-order Job Location typeahead, where the owner name is the primary
 * row label and the lot is the secondary line:
 *
 *   ┌───────────────────────────────────────┐
 *   │ <-- this function's output -->        │
 *   │ 742PS · 742 Pine Street               │
 *   └───────────────────────────────────────┘
 *
 * Input comes from the global-search API, which aggregates every person linked to the
 * account via `GROUP_CONCAT(first_name || ' ' || last_name, ', ')` — so a two-owner lot
 * arrives as the single string `"John Smith, Jane Smith"`.
 *
 * IMPORTANT: this output is DISPLAY ONLY. It is never written into the Job Location
 * field — that always gets the shortcode/address via the option's `fillValue`. So this
 * can be as human-friendly as you like without risking a person's name being saved as
 * a work-order location.
 */

/** Owner names exactly as the search API returned them, comma-separated. May be empty. */
export type RawOwnerNames = string | null | undefined;

/** A dropdown row is one line in a narrow field; past this the lot underneath suffers. */
const MAX_LABEL_LENGTH = 40;

/** Cut on a word boundary so a trust name ends in a word, not a stray letter. */
const truncate = (label: string): string => {
	if (label.length <= MAX_LABEL_LENGTH) return label;
	const cut = label.slice(0, MAX_LABEL_LENGTH);
	const lastSpace = cut.lastIndexOf(' ');
	return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

/** Everything after the first token — absent for single-token entity names ("Acme"). */
const surnameOf = (name: string): string => {
	const parts = name.split(' ');
	return parts.length > 1 ? parts[parts.length - 1] : '';
};

/**
 * Render an account's owners as one scannable line.
 *
 * Empty input returns `''`, which the caller reads as "fall back to the address".
 */
export const formatOwnerNames = (raw: RawOwnerNames): string => {
	const names = (raw ?? '')
		.split(',')
		.map((name) => name.trim().replace(/\s+/g, ' '))
		.filter(Boolean);

	if (names.length === 0) return '';

	// Three-plus owners never fit, and the trailing lot line matters more than name four.
	if (names.length >= 3) return truncate(`${names[0]} +${names.length - 1}`);

	if (names.length === 2) {
		const [first, second] = names;
		const firstSurname = surnameOf(first);
		const secondSurname = surnameOf(second);
		// "John Smith, Jane Smith" → "John & Jane Smith", the way staff say it out loud.
		// The surname keeps the FIRST owner's spelling; re-casing would break McDonald.
		if (firstSurname && firstSurname.toLowerCase() === secondSurname.toLowerCase()) {
			const firstGiven = first.slice(0, first.length - firstSurname.length).trim();
			const secondGiven = second.slice(0, second.length - secondSurname.length).trim();
			return truncate(`${firstGiven} & ${secondGiven} ${firstSurname}`);
		}
		return truncate(`${first}, ${second}`);
	}

	return truncate(names[0]);
};
